import React, { useState, useContext } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  Paper,
  Switch,
  FormControlLabel,
  Button,
  Slider,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Alert,
  Snackbar,
  IconButton,
  useTheme,
  alpha,
  Avatar,
  Stack,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  NightsStay as DarkModeIcon,
  WbSunny as LightModeIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  LockReset as LockResetIcon,
  Notifications as NotificationsIcon,
  Speed as SpeedIcon,
  Close as CloseIcon,
  Language as LanguageIcon,
  Security as SecurityIcon,
  Backup as BackupIcon,
  CreditCard as CreditCardIcon,
  History as HistoryIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { ThemeContext } from '../contexts/ThemeContext';
import { AuthContext } from '../contexts/AuthContext';

export default function Settings() {
  const theme = useTheme();
  const { isDarkMode, toggleTheme } = useContext(ThemeContext);
  const { user, logout } = useContext(AuthContext);
  
  // State for user settings
  const [refreshInterval, setRefreshInterval] = useState(300); // In seconds
  const [emailNotifications, setEmailNotifications] = useState(false);
  const [alertThreshold, setAlertThreshold] = useState(90); // In percentage
  const [timeZone, setTimeZone] = useState('UTC');
  
  // State for password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // State for snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // Handle snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };
  
  // Handle save settings
  const handleSaveSettings = () => {
    // In a real application, you would save these settings to a database
    setSnackbar({
      open: true,
      message: 'Settings saved successfully',
      severity: 'success',
    });
  };
  
  // Handle password change
  const handlePasswordChange = (e) => {
    e.preventDefault();
    
    // Validate passwords
    if (!currentPassword) {
      setSnackbar({
        open: true,
        message: 'Please enter your current password',
        severity: 'error',
      });
      return;
    }
    
    if (!newPassword) {
      setSnackbar({
        open: true,
        message: 'Please enter a new password',
        severity: 'error',
      });
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setSnackbar({
        open: true,
        message: 'New passwords do not match',
        severity: 'error',
      });
      return;
    }
    
    // In a real application, you would submit this to an API
    setSnackbar({
      open: true,
      message: 'Password changed successfully',
      severity: 'success',
    });
    
    // Clear form
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  return (
    <Box>
      {/* Page Header */}
      <Paper 
        sx={{ 
          p: 2, 
          mb: 3, 
          borderRadius: '12px', 
          backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
          backdropFilter: 'blur(10px)',
          boxShadow: `0 8px 16px ${alpha('#000', 0.1)}`,
        }}
      >
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <Box
              sx={{
                width: 40,
                height: 40,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.palette.primary.main,
              }}
            >
              <PersonIcon />
            </Box>
          </Grid>
          <Grid item>
            <Typography variant="h5" fontWeight="bold" sx={{ 
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              User Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Customize your experience
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        {/* User Profile */}
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            height: '100%', 
            borderRadius: '12px',
            backgroundColor: theme.palette.background.paper,
            boxShadow: `0 8px 16px ${alpha('#000', 0.1)}`,
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                  }}
                >
                  <PersonIcon />
                </Box>
                <Typography variant="h6" fontWeight="medium">
                  User Profile
                </Typography>
              </Box>
              <Divider sx={{ mb: 3, opacity: 0.1 }} />
              
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                mb: 3,
              }}>
                <Avatar 
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                    color: theme.palette.primary.main,
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    mb: 2,
                  }}
                >
                  U
                </Avatar>
                <Typography variant="h6" fontWeight="medium" gutterBottom>
                  {user?.username || 'User'}
                </Typography>
                <Chip 
                  label={user?.role || 'Admin'} 
                  size="small"
                  sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    fontWeight: 'medium',
                    borderRadius: '6px',
                  }}
                />
              </Box>
              
              <Typography variant="subtitle1" fontWeight="medium" mb={1}>
                Account Information
              </Typography>
              
              <Stack spacing={2} sx={{ mb: 3 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Email
                  </Typography>
                  <Typography variant="body1">
                    user@example.com
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Role
                  </Typography>
                  <Typography variant="body1">
                    {user?.role || 'Administrator'}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Last Login
                  </Typography>
                  <Typography variant="body1">
                    Today, 10:45 AM
                  </Typography>
                </Box>
              </Stack>
              
              <Button
                fullWidth
                variant="outlined"
                color="error"
                startIcon={<LockResetIcon />}
                onClick={logout}
                sx={{
                  borderRadius: '10px',
                  mt: 2,
                  textTransform: 'none',
                  fontWeight: 'medium',
                }}
              >
                Logout
              </Button>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Theme Settings */}
        <Grid item xs={12} md={8}>
          <Card sx={{ 
            height: '100%', 
            borderRadius: '12px',
            backgroundColor: theme.palette.background.paper,
            boxShadow: `0 8px 16px ${alpha('#000', 0.1)}`,
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                {isDarkMode ? (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                    }}
                  >
                    <DarkModeIcon />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                    }}
                  >
                    <LightModeIcon />
                  </Box>
                )}
                <Typography variant="h6" fontWeight="medium">
                  Appearance
                </Typography>
              </Box>
              <Divider sx={{ mb: 3, opacity: 0.1 }} />
              
              <Box 
                sx={{ 
                  p: 3, 
                  borderRadius: '10px', 
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                  mb: 3,
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={isDarkMode}
                      onChange={toggleTheme}
                      color="primary"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: theme.palette.primary.main,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.5),
                        },
                      }}
                    />
                  }
                  label={isDarkMode ? 'Dark Mode' : 'Light Mode'}
                  sx={{ fontWeight: 'medium' }}
                />
                <Typography variant="body2" color="text.secondary" mt={1}>
                  {isDarkMode
                    ? 'A darker color theme that is easier on the eyes in low-light environments.'
                    : 'A lighter color theme with better contrast and visibility in well-lit environments.'}
                </Typography>
              </Box>
              
              <Typography variant="h6" fontWeight="medium" mb={2}>
                Dashboard Settings
              </Typography>
              
              <Box mb={3}>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Dashboard Refresh Interval
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Set how often the dashboard should refresh data (in seconds)
                </Typography>
                <Box display="flex" alignItems="center" sx={{ mt: 2 }}>
                  <RefreshIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Slider
                    value={refreshInterval}
                    onChange={(e, newValue) => setRefreshInterval(newValue)}
                    min={30}
                    max={900}
                    step={30}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${value} sec`}
                    sx={{ 
                      ml: 2, 
                      flexGrow: 1,
                      '& .MuiSlider-thumb': {
                        backgroundColor: theme.palette.primary.main,
                      },
                      '& .MuiSlider-track': {
                        backgroundColor: theme.palette.primary.main,
                      },
                      '& .MuiSlider-rail': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                      },
                    }}
                  />
                  <Typography variant="body2" fontWeight="medium" ml={2} width={60}>
                    {refreshInterval} sec
                  </Typography>
                </Box>
              </Box>
              
              <Box mb={3}>
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Time Zone
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Set your preferred time zone for date and time display
                </Typography>
                <FormControl 
                  fullWidth 
                  size="small" 
                  sx={{ 
                    mt: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      backgroundColor: alpha(theme.palette.background.default, 0.5),
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.text.primary, 0.1),
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  <InputLabel 
                    id="timezone-label"
                    sx={{
                      color: theme.palette.text.secondary,
                    }}
                  >
                    Time Zone
                  </InputLabel>
                  <Select
                    labelId="timezone-label"
                    id="timezone"
                    value={timeZone}
                    label="Time Zone"
                    onChange={(e) => setTimeZone(e.target.value)}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: theme.palette.background.paper,
                          boxShadow: `0 4px 20px ${alpha('#000', 0.2)}`,
                          maxHeight: 300,
                        },
                      },
                    }}
                  >
                    <MenuItem value="UTC">UTC</MenuItem>
                    <MenuItem value="America/New_York">Eastern Time (ET)</MenuItem>
                    <MenuItem value="America/Chicago">Central Time (CT)</MenuItem>
                    <MenuItem value="America/Denver">Mountain Time (MT)</MenuItem>
                    <MenuItem value="America/Los_Angeles">Pacific Time (PT)</MenuItem>
                    <MenuItem value="Europe/London">London (GMT)</MenuItem>
                    <MenuItem value="Asia/Dubai">Dubai (GST)</MenuItem>
                    <MenuItem value="Asia/Kolkata">India (IST)</MenuItem>
                    <MenuItem value="Asia/Singapore">Singapore (SGT)</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveSettings}
                sx={{ 
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 'medium',
                  bgcolor: theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark,
                  },
                }}
              >
                Save Dashboard Settings
              </Button>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Alert Settings */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%', 
            borderRadius: '12px',
            backgroundColor: theme.palette.background.paper,
            boxShadow: `0 8px 16px ${alpha('#000', 0.1)}`,
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                  }}
                >
                  <NotificationsIcon />
                </Box>
                <Typography variant="h6" fontWeight="medium">
                  Notification Settings
                </Typography>
              </Box>
              <Divider sx={{ mb: 3, opacity: 0.1 }} />
              
              <Box mb={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={emailNotifications}
                      onChange={(e) => setEmailNotifications(e.target.checked)}
                      color="primary"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: theme.palette.primary.main,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.5),
                        },
                      }}
                    />
                  }
                  label="Email Notifications"
                  sx={{ fontWeight: 'medium' }}
                />
                <Typography variant="body2" color="text.secondary" mt={1} mb={2}>
                  Receive email notifications when alerts are triggered.
                </Typography>
                
                <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                  Alert Threshold
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Set the threshold for CPU, Memory, and Disk usage alerts
                </Typography>
                <Box display="flex" alignItems="center" sx={{ mt: 2 }}>
                  <Slider
                    value={alertThreshold}
                    onChange={(e, newValue) => setAlertThreshold(newValue)}
                    min={50}
                    max={95}
                    step={5}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${value}%`}
                    sx={{ 
                      flexGrow: 1,
                      '& .MuiSlider-thumb': {
                        backgroundColor: theme.palette.primary.main,
                      },
                      '& .MuiSlider-track': {
                        backgroundColor: theme.palette.primary.main,
                      },
                      '& .MuiSlider-rail': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                      },
                    }}
                  />
                  <Box 
                    sx={{ 
                      ml: 2, 
                      py: 0.5, 
                      px: 1.5, 
                      borderRadius: '6px', 
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      fontWeight: 'medium',
                    }}
                  >
                    {alertThreshold}%
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  You'll receive alerts when resource usage exceeds this threshold.
                </Typography>
              </Box>
              
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveSettings}
                sx={{ 
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 'medium',
                  bgcolor: theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark,
                  },
                }}
              >
                Save Notification Settings
              </Button>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Password Change */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%', 
            borderRadius: '12px',
            backgroundColor: theme.palette.background.paper,
            boxShadow: `0 8px 16px ${alpha('#000', 0.1)}`,
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                  }}
                >
                  <LockResetIcon />
                </Box>
                <Typography variant="h6" fontWeight="medium">
                  Change Password
                </Typography>
              </Box>
              <Divider sx={{ mb: 3, opacity: 0.1 }} />
              
              <Box component="form" onSubmit={handlePasswordChange}>
                <Stack spacing={2.5}>
                  <TextField
                    fullWidth
                    label="Current Password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    size="medium"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        backgroundColor: alpha(theme.palette.background.default, 0.5),
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: alpha(theme.palette.text.primary, 0.1),
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    label="New Password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    size="medium"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        backgroundColor: alpha(theme.palette.background.default, 0.5),
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: alpha(theme.palette.text.primary, 0.1),
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    size="medium"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        backgroundColor: alpha(theme.palette.background.default, 0.5),
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: alpha(theme.palette.text.primary, 0.1),
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                </Stack>
                
                <Box display="flex" justifyContent="flex-end" mt={3}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    sx={{ 
                      borderRadius: '10px',
                      textTransform: 'none',
                      fontWeight: 'medium',
                      bgcolor: theme.palette.primary.main,
                      '&:hover': {
                        bgcolor: theme.palette.primary.dark,
                      },
                    }}
                  >
                    Change Password
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            borderRadius: '10px',
            boxShadow: `0 4px 12px ${alpha('#000', 0.2)}`,
            fontWeight: 'medium',
          }}
          action={
            <IconButton
              size="small"
              color="inherit"
              onClick={handleSnackbarClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}